import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  closeDeleteDocumentsModal,
  setIsDeleteDocumentsModalOpen,
} from '@/redux/collections/actions';
import { selectIsDeleteDocumentsModalOpen } from '@/redux/collections/selectors';

interface DeleteDocumentsModalProps {
  /** Callback when deletion is confirmed */
  onConfirm: () => void;
  /** Whether deletion is in progress */
  isDeleting?: boolean;
}

/**
 * Modal for confirming bulk deletion of multiple documents
 */
export const DeleteDocumentsModal = ({
  onConfirm,
  isDeleting = false,
}: DeleteDocumentsModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const open = useSelector(selectIsDeleteDocumentsModalOpen);

  const closeModal = () => {
    dispatch(closeDeleteDocumentsModal());
  };

  const handleConfirm = () => {
    onConfirm();
  };

  const handleOpenChange = (open: boolean) => {
    dispatch(setIsDeleteDocumentsModalOpen(open));
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="gap-6 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('collections.modals.delete_content_title')}</DialogTitle>
          <DialogDescription>{t('collections.modals.delete_multipe_subtitle')}</DialogDescription>
        </DialogHeader>

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost-neutral"
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            className="text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              handleConfirm();
            }}
            isLoading={isDeleting}
          >
            {t('common.delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
